import React from 'react'
import { getCookie } from 'cookies-next'
import { When } from 'react-if'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { CaptionMD } from '@/atoms/Text'
import { SEPARATE_TIER_INTERVAL } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { RegionalAvailabilityWarning } from '@/organisms/RegionalAvailabilityWarning'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildPlanSelectionTiersStacked } from '@/views/GuildJoinView/GuildPlanSelectionTiersStacked'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { GuildPromotionBanner } from '../GuildJoinView/GuildPromotionBanner'

interface GuildPricingViewProps {
  heading: React.ReactNode
  isSignupLayout?: boolean
  canShowFreeOption?: boolean
  isOnlyBasic?: boolean
}

export const GuildPricingView: React.FC<GuildPricingViewProps> = ({
  heading,
  isSignupLayout,
  canShowFreeOption,
  isOnlyBasic,
}) => {
  const { shouldShowAvailabilityWarning, experiments } = useGuildSignupContext()
  const { t } = useTranslate('guild')

  const separateTierInterval = getCookie(SEPARATE_TIER_INTERVAL)
  const separateTierIntervalPriceSelection =
    separateTierInterval != null
      ? Boolean(separateTierInterval)
      : Boolean(experiments['separate-tier-interval-price-selection'])

  return (
    <main className="bg-core-gray-200 lg:min-h-screen">
      <div>
        <GuildPromotionBanner hideNavigation mobileClassName="!relative !bg-white" />
      </div>
      <section className="h-full min-h-screen w-full">
        <div className="hidden py-3 sm:mx-6 sm:py-4 md:flex md:justify-start">
          <InternalLink aria-label="Angel Studios" href={paths.home}>
            <AngelLogo height={40} color="black" />
          </InternalLink>
        </div>
        <div>
          {heading}

          <div className="mt-4 w-full md:mt-8">
            <When condition={shouldShowAvailabilityWarning}>
              <RegionalAvailabilityWarning className="mx-auto w-fit max-w-[800px]" />
            </When>
            <When condition={separateTierIntervalPriceSelection}>
              <GuildPlanSelectionTiersStacked
                className="mt-6 max-w-[640px] sm:mx-auto md:mt-0 lg:max-w-[840px] lg:px-4"
                darkMode={false}
                hidePremium={isOnlyBasic}
                signupLayout={isSignupLayout}
                hideLabels
                disclosePriceInUSD={isOnlyBasic}
                isSignupLayout={isSignupLayout}
                separateTierPlan
              />
            </When>
            <When condition={!separateTierIntervalPriceSelection}>
              <GuildPlanSelectionTiersStacked
                className="mt-6 max-w-[640px] sm:mx-auto md:mt-0 lg:max-w-[840px] lg:px-4"
                darkMode={false}
                hidePremium={isOnlyBasic}
                signupLayout={isSignupLayout}
                hideLabels
                disclosePriceInUSD={isOnlyBasic}
                isSignupLayout={isSignupLayout}
              />
            </When>
          </div>

          <When condition={canShowFreeOption}>
            <div className="w-full items-center justify-center px-4 pb-10 pt-2 text-center md:pb-[138px] md:pt-6">
              <CaptionMD weight="medium" className="md:photon-caption-lg">
                <Translate t={t} i18nKey="notInterestedPaidJoinFreeShowsFilms">
                  Not interested in a paid membership? - Click{' '}
                  <a href={paths.watch.index} className="underline">
                    here
                  </a>{' '}
                  to watch our free shows & films
                </Translate>
              </CaptionMD>
            </div>
          </When>
        </div>
      </section>
    </main>
  )
}
